import React from "react";
import { Activity, Plus, Trash2, Edit2, ArrowRight } from "lucide-react";

// Allowances created as "Gross" get a conversion note appended to their
// description by the backend, e.g. "(Gross 1200.00 EGP → Net 959.40 EGP,
// tax-shielded)". Pull that out so it can be rendered as its own styled
// chip instead of sitting inline in the free-text description.
const CONVERSION_NOTE_RE =
  /\(Gross\s+([\d,.]+)\s*EGP\s*(?:→|->)\s*Net\s+([\d,.]+)\s*EGP,\s*tax-shielded\)/i;
const splitConversionNote = (description) => {
  if (!description) return { text: "", grossAmount: null, netAmount: null };
  const match = description.match(CONVERSION_NOTE_RE);
  if (!match) return { text: description, grossAmount: null, netAmount: null };
  return {
    text: description.replace(match[0], "").trim(),
    grossAmount: match[1],
    netAmount: match[2],
  };
};

const AllowancesList = ({
  t,
  currentTheme,
  formStyles,
  isRTL,
  loading,
  allowances,
  onDeleteAllowance,
  onEditAllowance,
}) => {
  return (
    <>
      {/* Allowances List */}
      <div style={formStyles.section}>
        <h3
          style={{
            margin: "0 0 20px 0",
            color: currentTheme.colors.textPrimary,
            fontSize: "20px",
            fontWeight: "700",
          }}
        >
          {t("currentAllowances") || "Current Allowances"} ({allowances.length})
        </h3>

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: currentTheme.colors.textSecondary,
            }}
          >
            <Activity
              size={32}
              style={{ animation: "spin 1s linear infinite" }}
            />
            <p>{t("loading")}</p>
          </div>
        ) : allowances.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: currentTheme.colors.textSecondary,
            }}
          >
            <Plus size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
            <p>{t("noAllowances") || "No allowances added"}</p>
          </div>
        ) : (
          allowances.map((allowance, index) => {
            const { text: cleanDescription, grossAmount, netAmount } =
              splitConversionNote(allowance.description);
            const accentColor = currentTheme.colors.success;

            return (
              <div
                key={allowance._id || allowance.id || index}
                style={formStyles.allowanceCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = currentTheme.shadows.card;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Accent bar */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: "4px",
                    background: accentColor,
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "10px",
                        flexWrap: "wrap",
                        rowGap: "6px",
                      }}
                    >
                      <span
                        style={{
                          background: `${accentColor}18`,
                          color: accentColor,
                          padding: "5px 11px",
                          borderRadius: "8px",
                          fontSize: "11px",
                          fontWeight: "700",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(allowance.type)}
                      </span>

                      {allowance.isTaxable === false ? (
                        <span
                          title="Net — added straight to the final net pay, no tax conversion applied."
                          style={{
                            background: "rgba(16, 185, 129, 0.15)",
                            color: "#10b981",
                            padding: "5px 10px",
                            borderRadius: "8px",
                            fontSize: "11px",
                            fontWeight: "700",
                            cursor: "help",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("net") || "Net"}
                          <span style={{ opacity: 0.7, fontWeight: 400 }}>ⓘ</span>
                        </span>
                      ) : allowance.isTaxable ? (
                        <span
                          title={
                            allowance.netConverted
                              ? "Entered as Gross. The amount shown is already the converted net-equivalent impact (tax-shielded) — smaller than the Gross figure originally entered."
                              : "Gross allowance — grossed up onto taxable income before tax/insurance are calculated, so take-home pay rises by less than the amount shown."
                          }
                          style={{
                            background: "rgba(139, 92, 246, 0.15)",
                            color: "#8b5cf6",
                            padding: "5px 10px",
                            borderRadius: "8px",
                            fontSize: "11px",
                            fontWeight: "700",
                            cursor: "help",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("gross") || "Gross"}
                          <span style={{ opacity: 0.7, fontWeight: 400 }}>ⓘ</span>
                        </span>
                      ) : null}

                      {allowance.days ? (
                        <span
                          style={{
                            fontSize: "12px",
                            color: currentTheme.colors.textMuted,
                            background: currentTheme.colors.inputBackground,
                            padding: "5px 10px",
                            borderRadius: "8px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {allowance.days} {t("days")}
                        </span>
                      ) : null}

                      {allowance.hours ? (
                        <span
                          style={{
                            fontSize: "12px",
                            color: currentTheme.colors.textMuted,
                            background: currentTheme.colors.inputBackground,
                            padding: "5px 10px",
                            borderRadius: "8px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {allowance.hours} {t("hours") || "hrs"}
                        </span>
                      ) : null}

                      {allowance.percentage ? (
                        <span
                          style={{
                            fontSize: "12px",
                            color: currentTheme.colors.textMuted,
                            background: currentTheme.colors.inputBackground,
                            padding: "5px 10px",
                            borderRadius: "8px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {allowance.percentage}%
                        </span>
                      ) : null}

                      {allowance.isRecurring ? (
                        <span
                          style={{
                            background: `${currentTheme.colors.info}20`,
                            color: currentTheme.colors.info,
                            padding: "5px 10px",
                            borderRadius: "8px",
                            fontSize: "11px",
                            fontWeight: "700",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t("recurring") || "Recurring"}
                        </span>
                      ) : null}
                    </div>

                    <div
                      style={{
                        fontSize: "14.5px",
                        color: currentTheme.colors.textPrimary,
                        marginBottom: grossAmount ? "8px" : "10px",
                        fontWeight: "600",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                      }}
                    >
                      {cleanDescription || t("noDescription")}
                    </div>

                    {grossAmount && netAmount ? (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#8b5cf6",
                          background: "rgba(139, 92, 246, 0.1)",
                          border: "1px solid rgba(139, 92, 246, 0.25)",
                          borderRadius: "8px",
                          padding: "4px 10px",
                          marginBottom: "10px",
                        }}
                      >
                        <span>{grossAmount} {t("currency")}</span>
                        <ArrowRight size={12} />
                        <span>{netAmount} {t("currency")}</span>
                        <span style={{ opacity: 0.75, fontWeight: 500 }}>
                          tax-shielded
                        </span>
                      </div>
                    ) : null}

                    <div
                      style={{
                        fontSize: "12.5px",
                        color: currentTheme.colors.textMuted,
                      }}
                    >
                      {t("date")}:{" "}
                      {allowance.attendance?.date
                        ? new Date(allowance.attendance.date).toLocaleDateString(
                            isRTL ? "ar-EG" : "en-GB"
                          )
                        : allowance.created_at
                        ? new Date(allowance.created_at).toLocaleDateString(
                            isRTL ? "ar-EG" : "en-GB"
                          )
                        : "N/A"}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: "10px",
                      flex: "0 0 auto",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <div
                        style={{
                          fontSize: "22px",
                          fontWeight: "800",
                          color: accentColor,
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                        }}
                      >
                        +{Number(allowance.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}{" "}
                        {t("currency")}
                      </div>
                      {allowance.isTaxable && allowance.netConverted ? (
                        <div
                          style={{
                            fontSize: "10.5px",
                            color: currentTheme.colors.textMuted,
                            marginTop: "2px",
                            fontStyle: "italic",
                          }}
                          title="This is the employee's actual take-home-pay impact after tax-shielding the Gross amount entered — not the Gross figure itself."
                        >
                          Net impact (tax-shielded)
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditAllowance) onEditAllowance(allowance);
                        }}
                        style={{
                          background: "transparent",
                          border: `1px solid ${currentTheme.colors.border}`,
                          cursor: "pointer",
                          color: currentTheme.colors.textSecondary,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            currentTheme.colors.hover || "rgba(0,0,0,0.05)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                        title={t("edit")}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDeleteAllowance)
                            onDeleteAllowance(allowance._id || allowance.id);
                        }}
                        style={{
                          background: "transparent",
                          border: `1px solid ${currentTheme.colors.border}`,
                          cursor: "pointer",
                          color: currentTheme.colors.textSecondary,
                          padding: "6px 12px",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            currentTheme.colors.hover || "rgba(0,0,0,0.05)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                        title={t("delete")}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default AllowancesList;
